import { enqueueSnackbar } from "notistack";
import BillingAddressForm from "../billing/BillingAddressForm";
import { useDispatch, useSelector } from "react-redux";
import { getOrgURLParam, parseNumber, validatePhoneNumber } from "../../utility-functions";
import { useEffect, useMemo, useState } from "react";
import { JService } from "../api-service/ApiService";
import { setAllOrgsDetails, setSubscriptionDetails } from "../features/account/account";
import { states } from "../billing/India-states";
import Button from "../design/Button";
import PurchaseSummary from "./PurchaseSummary";
import { arrowRight, chevronLeft } from "../../utility-functions/assetLoader";
import colors from "../design/colors";

const BillingAddress = ({onContinue, onBack, isKycProcessCompleted, ...finalPurchaseData}) => {

    const is_inr = finalPurchaseData?.currency === "₹";

    const reduxSubscription = useSelector(
        (state) => state?.account?.value?.subscription,
    );
    const reduxAccount = useSelector((state) => state?.account?.value);
    const dispatch = useDispatch();

    const billingAddress = reduxSubscription?.billing_address || {};

    const savedBillingNumber = reduxSubscription?.billing_number || '';
    
    const {cc: savedCC, number: savedNum} = useMemo(() => parseNumber(savedBillingNumber), [savedBillingNumber]);
    

    const [companyName, setCompanyName] = useState({ value: "", error: null });
    const [gstNumber, setGstNumber] = useState(reduxSubscription?.gstin || "");
    const [address, setAddress] = useState({
        line1: billingAddress.line1 || "",
        line2: billingAddress.line2 || "",
        city: billingAddress.city || "",
        zip: billingAddress.zip || "",
        state: billingAddress.state || "",
        country: billingAddress.country || "India",
    });
    const [billingNumber, setBillingNumber] = useState({ value: savedNum || "" });
    const [countryCodeDropdown, setCountryCodeDropdown] = useState(savedCC || "+1");

    const [companyNameModified, setCompanyNameModified] = useState(false);
    const [addressModified, setAddressModified] = useState(false);
    const [isDisabled, setIsDisabled] = useState(true);
    const [isLoading, setIsLoading] = useState(false);

    const handleCompanyNameChange = (e, val) => {
        if (e === "focus") {
            setCompanyName((prev) => ({ ...prev, error: null }));
            return;
        } else if (e === "value") {
            setCompanyName({ value: val, error: null });
            return;
        }
        if (!companyNameModified) setCompanyNameModified(true);
        setCompanyName((prev) => ({ ...prev, value: e.target.value }));
    };

    const handleAddressChange = (e, field) => {
        if (!addressModified) setAddressModified(true);
        if (field === "country") {
            setAddress((prev) => ({ ...prev, country: e }));
            return;
        }
        const name = e.target.name;
        setAddress((prev) => ({ ...prev, [name]: e.target.value }));
    };

    const handleGstNumberChange = (val) => {
        if (!addressModified) setAddressModified(true);
        setGstNumber(val);
    };

    const handleBillingNumberChange = (val) => {
        if (!addressModified) setAddressModified(true);
        setBillingNumber(val);
    };

    const handleCountryCodeDropdownChange = (val) => {
        if (!addressModified) setAddressModified(true);
        setCountryCodeDropdown(val);
    };

    const fetchBillingDetails = () => {
        JService.get(`/api/v1/subscriptions/billing-details/${getOrgURLParam()}`)
        .then((res) => {
            if (!res.success) return;
            let temp = { ...res?.data };
            delete temp["numbers"];
            temp["addon_licenses"] = [...(temp?.addons || [])];
            delete temp["addons"];
            temp["plan_licenses"] = { ...temp?.plan?.licenses };
            temp["viewer_licenses"] = { ...temp?.viewers?.licenses };
            if (temp && Object.keys(temp)?.length) {
            dispatch(setSubscriptionDetails(temp));
            }
        })
        .catch((err) => {
            console.error("Error while fetching billing details:", err);
        });
    };


    const updateCompanyName = async () => {
        let errors = null;
        try {
            const res = await JService.patch(
            `/api/v1/auth/update-organization/${getOrgURLParam()}`,
            JSON.stringify({ company: companyName.value }),
            );
            if (!res?.success) {
            if (res?.details) errors = res?.details;
            throw new Error(
                res?.message || res?.detail || "Could not update company details",
            );
            }
            let allOrgsData = [
            ...reduxAccount?.allOrgs?.filter((ao) => ao?.id !== res.data?.id),
            res.data,
            ];
            dispatch(setAllOrgsDetails(allOrgsData));
        } catch (err) {
            if (errors && errors?.company) {
            setCompanyName((prev) => ({
                ...prev,
                error: Array.isArray(errors?.company)
                ? errors?.company[0]
                : errors.company,
            }));
            } else {
            enqueueSnackbar(err?.message, { variant: "error" });
            }
            throw new Error("Failed to update company name");
        }
    };

    const updateBillingAddress = async () => {
        try {
            if (!address.country || !address.state || !address.zip) {
            throw new Error("Please enter valid values for all address fields");
            }
            const payload = {
            address: {
                line1: address.line1 || "",
                line2: address.line2 || "",
                zip: address.zip || "",
                city: address.city || "",
                state: address.state || "",
                country: address.country || "",
                state_code: states[address.state],
            },
            gstin: gstNumber || null,
            };

            if (!validatePhoneNumber(countryCodeDropdown, billingNumber.value)) {
            throw new Error("Please enter valid billing number.");
            }

            payload.billing_number = countryCodeDropdown + billingNumber.value;

            if (
            (!payload.address.state || !payload.address.state?.trim().length) &&
            is_inr
            ) {
            throw new Error("Please enter a valid value for state");
            }
            const res = await JService.patch(
            `/api/v1/subscriptions/subscription/${getOrgURLParam()}`,
            JSON.stringify(payload),
            );
            if (!res?.success) {
            throw new Error(res?.message || "Failed to update the billing address");
            }
            fetchBillingDetails();
            enqueueSnackbar(
            res?.message || "Successfully updated the billing information",
            { variant: "success" },
            );
        } catch (err) {
            setAddress({
            line1: billingAddress.line1 || "",
            line2: billingAddress.line2 || "",
            city: billingAddress.city || "",
            zip: billingAddress.zip || "",
            state: billingAddress.state || "",
            country: billingAddress.country || "India",
            });
            setBillingNumber({value: savedNum});
            setCountryCodeDropdown(savedCC);
            setGstNumber(reduxSubscription?.gstin || "");
            throw new Error(err?.message || "Failed to update billing address");
        }
    };
    

    const onSubmit = async () => {
        try {
            setIsLoading(true);
            if (companyNameModified) {
                await updateCompanyName();
                setCompanyNameModified(false);
            }
            if (addressModified) {
                await updateBillingAddress();
                setAddressModified(false);
            }

            onContinue();
        }catch (err) {
            enqueueSnackbar(err?.message || "There was some error. Please try again!", {variant: 'error'});
        }finally {
            setIsLoading(false);
        }
    };

    // Validate form completeness
    useEffect(() => {
        let addressInvalid = false;
        for (let key in address) {
            if (key === "line2") continue;
            if (!address[key] || !address[key]?.trim()?.length) {
                addressInvalid = true;
                break;
            }
        }
        if (
        !companyName.value ||
        !companyName?.value?.trim()?.length ||
        addressInvalid
        ) {
            if (!isDisabled) setIsDisabled(true);
        } else {
            if (isDisabled) setIsDisabled(false);
        }
    }, [address, companyName]);

    return (
        <div>
            <div
                className="configure-back"
                style={{ marginTop: "20px" }}
                onClick={onBack}
            >
                {chevronLeft(14, 14, colors.nc_gray_800, {
                verticalAlign: "middle",
                })}{" "}
                Back to configure
            </div>
            
            <h3 className="section-title">Billing details</h3>

            <div className="configure-layout" style={{ marginTop: "16px" }}>

                {/* Left column — Billing address form */}
                <div className="configure-left" style={{ maxWidth: "580px" }}>
                    <BillingAddressForm
                        visible={true}
                        gstOptional={true}
                        companyName={companyName}
                        handleCompanyNameChange={handleCompanyNameChange}
                        address={address}
                        handleAddressChange={handleAddressChange}
                        gstNumber={gstNumber}
                        handleGstNumberChange={handleGstNumberChange}
                        billingNumber={billingNumber}
                        handleBillingNumberChange={handleBillingNumberChange}
                        countryCodeDrpdown={countryCodeDropdown}
                        handleCountryCodeDropdownChange={handleCountryCodeDropdownChange}
                        formWidth="100%"
                        stateRequired={is_inr}
                    />
                </div>

                {/* Right column — Purchase summary */}
                <div className="configure-right">
                <PurchaseSummary
                    {...finalPurchaseData}
                    isInr={is_inr}
                    HeaderButton={() => (
                    <Button
                        styleOverride={{ width: "100%", marginBottom: "5px" }}
                        hierarchy="green"
                        buttonText={"Continue to " + (isKycProcessCompleted ? "payment" : "verification")}
                        disabled={isDisabled || isLoading}
                        onClick={onSubmit}
                        isLoading={isLoading}
                        icon={{endIcon: arrowRight(colors.ic_white)}}
                    />
                    )}
                />
                </div>
            </div>
        </div>
    )
};

export default BillingAddress;